/**
 * Phase 71 partial — keyword-based mascot classifier.
 *
 * Pure function. No state, no I/O. Given a piece of text, returns the
 * set of L1 categories it triggers + which keywords matched. Designed
 * for the personal-moderation mascot's pre-LLM phase — Phase 70's
 * WebLLM upgrade swaps the keyword backend for Phi-3.5 inference
 * without changing this module's contract.
 *
 * Category list comes from `docs/alds-roadmap-v12.md` § Phase 71:
 * violence · sexual · doxing · hate · kid-safety · location-leak ·
 * scam-pattern.
 *
 * The keyword lists are deliberately small and conservative. False
 * negatives are preferred over false positives — the mascot is a
 * personal filter, not a moderator, and over-hiding erodes user trust
 * fast. Operators can extend a category at runtime via
 * `MascotManager.extendCategory()`.
 */

export type MascotCategory =
  | 'violence'
  | 'sexual'
  | 'doxing'
  | 'hate'
  | 'kid-safety'
  | 'location-leak'
  | 'scam-pattern';

export const MASCOT_CATEGORIES: readonly MascotCategory[] = [
  'violence', 'sexual', 'doxing', 'hate',
  'kid-safety', 'location-leak', 'scam-pattern',
] as const;

export interface ClassifierMatch {
  category: MascotCategory;
  matchedTerms: string[];
}

export interface ClassifierResult {
  categories: MascotCategory[];
  matches: ClassifierMatch[];
}

/**
 * Default keyword sets. Lower-case; matched against lower-cased input
 * using word-boundary scans.
 *
 * `kid-safety` and `location-leak` use regex tests rather than plain
 * keyword sets — they need shape detection (street addresses, school
 * grade levels, etc.) that word lists can't express cleanly.
 */
const DEFAULT_KEYWORDS: Readonly<Record<MascotCategory, readonly string[]>> = {
  violence: [
    'kill', 'shoot', 'beat up', 'attack', 'stab', 'murder', 'fight',
    'beat them', 'punch', 'gun', 'knife', 'assault',
  ],
  sexual: [
    'nude', 'naked', 'porn', 'sexual', 'sex', 'nsfw', 'explicit',
  ],
  doxing: [
    'home address', 'phone number', 'ssn', 'social security',
    'where they live', 'their address', 'real name is',
  ],
  hate: [
    'slur', 'nazi', 'genocide', 'subhuman', 'should die', 'should be killed',
  ],
  'kid-safety': [
    'send me a pic', 'how old are you', 'don\'t tell your parents',
    'our secret', 'meet me alone', 'come over',
  ],
  'location-leak': [
    'i live at', 'my address is', 'meet me at', 'my house at',
  ],
  'scam-pattern': [
    'wire transfer', 'send bitcoin', 'gift cards', 'crypto wallet',
    'urgent payment', 'verify your account', 'click this link',
    'you have won', 'limited time offer',
  ],
};

const STREET_ADDRESS_RE = /\b\d{1,5}\s+[A-Za-z][A-Za-z\s]{2,30}\s+(st|street|ave|avenue|rd|road|blvd|boulevard|ln|lane|dr|drive|ct|court|way|pkwy|parkway)\b/i;
const PHONE_RE = /(?:\+?\d{1,2}\s?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}/;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/;
const GRADE_RE = /\b(?:kindergarten|1st\s*grade|2nd\s*grade|3rd\s*grade|4th\s*grade|5th\s*grade|6th\s*grade|7th\s*grade|8th\s*grade|9th\s*grade|10th\s*grade|11th\s*grade|12th\s*grade|in\s*kindergarten|i\s+am\s+\d{1,2})\b/i;

/**
 * Classify a piece of text. Returns the categories triggered + the
 * matched terms per category. Empty `categories` array means clean.
 */
export function classify(
  text: string,
  keywordOverrides?: Partial<Record<MascotCategory, readonly string[]>>,
): ClassifierResult {
  const lower = text.toLowerCase();
  const out: ClassifierMatch[] = [];
  for (const category of MASCOT_CATEGORIES) {
    const keywords = keywordOverrides?.[category] ?? DEFAULT_KEYWORDS[category];
    const matched: string[] = [];
    for (const kw of keywords) {
      if (containsWordOrPhrase(lower, kw)) matched.push(kw);
    }
    if (matched.length > 0) {
      out.push({ category, matchedTerms: matched });
    }
  }
  // Regex-shape detectors layered on top of keyword sets. Each appends
  // a synthetic match term that's readable in the filter log.
  if (STREET_ADDRESS_RE.test(text)) {
    upsertMatch(out, 'location-leak', '[street-address-pattern]');
    upsertMatch(out, 'doxing', '[street-address-pattern]');
  }
  if (PHONE_RE.test(text)) {
    upsertMatch(out, 'doxing', '[phone-number-pattern]');
  }
  if (SSN_RE.test(text)) {
    upsertMatch(out, 'doxing', '[ssn-pattern]');
  }
  if (GRADE_RE.test(text)) {
    upsertMatch(out, 'kid-safety', '[school-grade-pattern]');
  }
  return {
    categories: out.map((m) => m.category),
    matches: out,
  };
}

function containsWordOrPhrase(haystack: string, needle: string): boolean {
  if (needle.includes(' ')) {
    // Multi-word phrase — substring check is fine; word boundaries
    // would prevent matching "send me a pic" inside "could you send
    // me a pic please".
    return haystack.includes(needle);
  }
  // Single word — require word boundary on at least one side to avoid
  // matching "killing" off "kill" if we wanted exact word; we DO want
  // substring matching for stems (kill → killing, killed), so a simple
  // includes() is correct here. The list itself is the precision lever.
  return haystack.includes(needle);
}

function upsertMatch(out: ClassifierMatch[], category: MascotCategory, term: string): void {
  const existing = out.find((m) => m.category === category);
  if (existing !== undefined) {
    if (!existing.matchedTerms.includes(term)) existing.matchedTerms.push(term);
  } else {
    out.push({ category, matchedTerms: [term] });
  }
}

/**
 * Returns the default keyword list for a category. Useful for the
 * mascot drawer surface to render "what this category looks for."
 */
export function defaultKeywordsFor(category: MascotCategory): readonly string[] {
  return DEFAULT_KEYWORDS[category];
}
